import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  addUserToGroup,
  deleteUserFromGroup,
  fetchAllUsers,
  fetchUserGroup,
  fetchUserGroupMembers,
  resetGroupMembers,
  selectAddingUser,
  selectFilteredUsers,
  selectLoadingUserGroup,
  selectLoadingUserGroupMembers,
  selectLoadingUsers,
  selectUserGroupMembers,
} from "./UserGroupMembersSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { SearchSelectbox } from "cpl-react-common/components/Inputs/SearchSelectbox";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import * as Yup from "yup";
import { ErrorMessage, Form, Formik } from "formik";
import { selectUserGroup } from "./UserGroupSlice";
import { useDispatch } from "react-redux";

type Props = {
  userGroupID: number | null;
};

const validationSchema = () =>
  Yup.object().shape(
    {
      newUserID: Yup.string().required("A valid user must be selected"),
    },
    []
  );

const UserGroupMembers: React.FC<Props> = ({ userGroupID }) => {
  const thunkDispatch = useThunkDispatch();
  const dispatch = useDispatch();
  const filteredUsers = useSelector(selectFilteredUsers);
  const userGroupMembers = useSelector(selectUserGroupMembers);
  const userGroup = useSelector(selectUserGroup);

  const loadingUsers = useSelector(selectLoadingUsers);
  const loadingUserGroup = useSelector(selectLoadingUserGroup);
  const loadingUserGroupMembers = useSelector(selectLoadingUserGroupMembers);
  const addingUser = useSelector(selectAddingUser);
  const groupIsBusy = loadingUserGroup || loadingUserGroupMembers || addingUser;
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  useEffect(() => {
    if (userGroupID != null) {
      thunkDispatch(fetchUserGroup(userGroupID));
      thunkDispatch(fetchUserGroupMembers(userGroupID));
      thunkDispatch(fetchAllUsers());
    } else {
      dispatch(resetGroupMembers());
    }
  }, [thunkDispatch, dispatch, userGroupID]);

  async function handleAddUser({ newUserID }: { newUserID: string | undefined }) {
    if (newUserID !== undefined && userGroupID != null) {
      thunkDispatch(addUserToGroup({ userID: newUserID, userGroupID }));
      setShowAddMemberModal(false);
    }
  }

  function removeUser(userID: string) {
    if (userGroupID != null) {
      thunkDispatch(deleteUserFromGroup({ userID, userGroupID }));
    }
  }

  return (
    <Permission permissionPredicate={(permission) => permission === Permissions.ViewUserGroups}>
      <div className="card">
        <ActivityIndicator isBusy={groupIsBusy}>
          <Permission permissionPredicate={(permission) => permission === Permissions.ManageUserGroupMembers}>
            <SimpleModal
              modalTitle="New User Group Member"
              showModal={showAddMemberModal}
              dismissModalCallback={() => setShowAddMemberModal(false)}
            >
              <Formik
                initialValues={{ newUserID: undefined }}
                validationSchema={validationSchema()}
                onSubmit={(values) => handleAddUser(values)}
                enableReinitialize={true}
              >
                {({ setFieldValue }) => {
                  return (
                    <Form>
                      <div className="row mb-6">
                        <label className="col-lg-3 col-form-label required fw-bold">User:</label>
                        <div className="col-lg-9">
                          <ActivityIndicator isBusy={loadingUsers}>
                            <SearchSelectbox
                              data-test-id="new-user-group-member-modal__user-select"
                              selectOptions={filteredUsers ?? []}
                              handleChange={(selection) => {
                                setFieldValue("newUserID", selection.value);
                              }}
                            />
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                <ErrorMessage name="newUserID" />
                              </div>
                            </div>
                          </ActivityIndicator>
                        </div>
                      </div>
                      <button
                        data-test-id="new-user-group-member-modal__add-button"
                        type="submit"
                        className="col-sm-3 btn btn-success btn-sm"
                      >
                        Add
                      </button>
                    </Form>
                  );
                }}
              </Formik>
            </SimpleModal>
          </Permission>
          <div className="card-header">
            <div className="card-title">
              {userGroup != undefined ? `${userGroup?.groupName} Members` : "No Group Selected"}
            </div>
            <div className="card-toolbar">
              <Permission permissionPredicate={(permission) => permission === Permissions.ManageUserGroupMembers}>
                <button
                  data-test-id="user-group-members-table__open-add-member-modal-button"
                  type="button"
                  className="btn btn-primary btn-icon btn-sm"
                  onClick={() => setShowAddMemberModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus as IconProp} />
                </button>
              </Permission>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                <tbody>
                  {userGroupMembers?.map((row) => (
                    <tr key={row.userID}>
                      <td className="text-dark fw-bold text-hover-primary mb-1 fs-6">
                        {row.firstName} {row.lastName}
                      </td>
                      <Permission
                        permissionPredicate={(permission) => permission === Permissions.ManageUserGroupMembers}
                      >
                        <td className="text-end">
                          <button
                            data-test-id={`user-group-members-table__remove-button-${row.userID}`}
                            className="btn btn-danger  btn-icon  btn-sm border-0 me-1"
                            onClick={() => removeUser(row.userID!)}
                          >
                            <FontAwesomeIcon icon={faTrash as IconProp} />
                          </button>
                        </td>
                      </Permission>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ActivityIndicator>
      </div>
    </Permission>
  );
};

export default UserGroupMembers;
